import { useQuery } from "@tanstack/react-query";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAuthToken } from "app/Auth";
import { client } from "common/apiClient";
import { AccountPlanResponse, SubscriptionPlan } from "./types";
import { PlanCategory, BillingPeriod } from "./useNativePackages";

/**
 * サブスクリプションプラン一覧を取得
 * GET /v1/user/subscription/plans?store=web
 */
export const fetchSubscriptionPlans = async (
  store: "web" | "app_store" | "play_store",
): Promise<SubscriptionPlan[]> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/user/subscription/plans`, {
    params: { store },
  });
  return response.data.plans;
};

/**
 * サブスクリプションプラン一覧を取得（React Query キャッシュ付き）
 */
export const useSubscriptionPlans = (
  store: "web" | "app_store" | "play_store",
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["subscriptionPlans", store],
    queryFn: () => fetchSubscriptionPlans(store),
    enabled,
    staleTime: 1000 * 60 * 60, // 1時間キャッシュ
  });
};

/**
 * アカウントのプラン情報を取得
 * GET /v1/user/account/:uid/plan
 *
 * @param uid - 親UID
 * @param forceSync - 強制同期フラグ
 */
export const fetchAccountPlan = createAsyncThunk<
  AccountPlanResponse,
  { uid: string; forceSync?: boolean }
>("subscription/fetchAccountPlan", async ({ uid, forceSync = false }) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/user/account/${uid}/plan`, {
    params: { force_sync: forceSync },
  });
  return response.data;
});

/**
 * Hosted Checkout Session作成
 * POST /v1/user/account/subscription/checkout/:uid
 */
export const createHostedCheckout = createAsyncThunk<
  { checkout_url: string },
  { uid: string; product_id: string; success_url: string; cancel_url: string }
>("subscription/createHostedCheckout", async ({ uid, product_id, success_url, cancel_url }) => {
  const token = await fetchAuthToken();
  const response = await client({ token }).post(`/v1/user/account/subscription/checkout/${uid}`, {
    product_id,
    success_url,
    cancel_url,
  });
  return response.data;
});

/**
 * カスタマーポータルセッション作成
 * POST /v1/user/account/subscription/portal/:uid
 */
export const createCustomerPortal = async ({
  uid,
  return_url,
}: {
  uid: string;
  return_url: string;
}): Promise<{ portal_url: string }> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).post(`/v1/user/account/subscription/portal/${uid}`, {
    return_url,
  });
  return response.data;
};

/**
 * 保護者へ課金のお願いを送信（メール＋チャットを同時送信）
 * POST /v1/user/account/subscription/request-purchase/:uid
 *
 * @param uid - 子どもUID（保護者はサーバ側で解決）
 * @param plan_category - プラン種別（省略時はプランなし＝保護者が課金ページで選ぶ）
 * @param billing_period - 課金周期
 */
export const requestPurchase = async ({
  uid,
  plan_category,
  billing_period,
}: {
  uid: string;
  plan_category?: PlanCategory;
  billing_period?: BillingPeriod;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(`/v1/user/account/subscription/request-purchase/${uid}`, {
    plan_category,
    billing_period,
  });
};
